import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ReleaseNotesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ReleaseNotesModal({ open, onOpenChange }: ReleaseNotesModalProps) {
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch("/changelog.md", { cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load changelog: ${res.status}`);
        const text = await res.text();
        if (!cancelled) setContent(text);
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (!cancelled) setError("Unable to load release notes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Release notes</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          {loading && (
            <div className="text-muted-foreground text-sm">Loading…</div>
          )}
          {error && (
            <div className="text-destructive text-sm" role="alert">{error}</div>
          )}
          {!loading && !error && (
            <ScrollArea className="max-h-[60vh] pr-4">
              <article className="space-y-4 text-sm leading-relaxed">
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1 className="text-2xl font-bold" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="text-xl font-semibold mt-4" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 className="text-lg font-semibold" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="list-disc pl-5 space-y-2" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="list-decimal pl-5 space-y-2" {...props} />
                    ),
                    a: ({ node, ...props }) => (
                      <a className="underline underline-offset-4" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="text-foreground/90" {...props} />
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </article>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
